import React, { Suspense, useState } from 'react';
import { observer } from 'mobx-react';
import { withNamespaces, WithNamespaces } from 'react-i18next';
import { ConnectionOverview } from '@wikifactory/voxel-ui';
import { useDesktopCompatibility } from 'hooks/useDesktopCompatible';
import { isInDesktopMode } from 'libs/gutClient/electronBridge';
import { LocalChangesContainer } from './LocalChangesContainer';
import { LocalDriveContainer } from './LocalDriveContainer';
import { CloudDriveContainer } from './CloudDriveContainer';
import { SyncedDropdown } from './SyncedDropdown';
import { SyncRepoAppManager } from '../appManager';
import { FileSideBar } from './FileSideBar';

interface PropsI extends WithNamespaces {
  repoManager: any;
}

export const SyncProjectContainer = withNamespaces()(
  observer(({ repoManager }: PropsI) => {
    const { syncRepoAppManager }: { syncRepoAppManager: SyncRepoAppManager } =
      repoManager;

    const compatibleWithTab = useDesktopCompatibility({
      feature: 'tab',
    });

    const desktopMode = isInDesktopMode();

    let status = syncRepoAppManager.connectionStatus;
    const download =
      syncRepoAppManager.downloadsProgress.size === 0
        ? null
        : {
            numFiles: syncRepoAppManager.downloadsProgress.size,
            // progress: 100, // TODO
          };

    const upload =
      syncRepoAppManager.uploadsProgress.size === 0 ||
      // even if we have upload updates ignore them if we are not contributing
      !syncRepoAppManager.contributingManager.isContributing
        ? null
        : {
            numFiles: syncRepoAppManager.numUploadsCompleted,
            totalFiles: syncRepoAppManager.totalExpectedUploads,
            // progress: 100, // TODO
          };

    if (syncRepoAppManager.connected) {
      if (download || upload) status = 'syncing';
      else if (syncRepoAppManager.contributingManager.isContributing)
        status = 'contributing';
      else if (syncRepoAppManager.pullingOrSyncingBlobs) status = 'pulling';
      else if (syncRepoAppManager.areThereNewRemoteCommits)
        status = 'remoteContributions';
    }

    const [sideBarFile, setSideBarFile] = useState(null);

    const isNotLocallyConnected = !syncRepoAppManager.connected;

    return (
      <Suspense fallback='loading'>
        <div
          style={{
            margin: '20px 0px',
          }}
        >
          <ConnectionOverview
            status={status}
            download={download}
            upload={upload}
            onConnectClick={() => {
              syncRepoAppManager.ConnectAndPull();
            }}
            onReconnectClick={() => {
              syncRepoAppManager.ConnectAndPull();
            }}
            onOpenLocalFolderClick={() => {
              syncRepoAppManager.openPath('', '/');
            }}
            rightActionWrapperClassName='flex items-center'
            rightAction={
              syncRepoAppManager.connected &&
              desktopMode &&
              compatibleWithTab && (
                <div className='inline-flex ml-2'>
                  <SyncedDropdown syncRepoAppManager={syncRepoAppManager} />
                </div>
              )
            }
          />
          {!syncRepoAppManager.connected ? (
            <CloudDriveContainer
              syncRepoAppManager={syncRepoAppManager}
              setSideBarFile={setSideBarFile}
            />
          ) : (
            <>
              <LocalChangesContainer syncRepoAppManager={syncRepoAppManager} />
              <div
                style={{
                  margin: '30px 0px',
                }}
              >
                <LocalDriveContainer
                  syncRepoAppManager={syncRepoAppManager}
                  setSideBarFile={setSideBarFile}
                />
              </div>
            </>
          )}
          <FileSideBar
            project={syncRepoAppManager.repoManager.parent.project}
            open={!!sideBarFile}
            file={sideBarFile}
            isNotLocallyConnected={isNotLocallyConnected}
            onClosePanel={() => {
              setSideBarFile(null);
            }}
            onRestoreFileToVersion={(file, contribution) => {
              syncRepoAppManager.contributingManager.ContributeRestoreFileToVersion(
                file,
                contribution,
              );
            }}
          />
        </div>
      </Suspense>
    );
  }),
);
